import React, {
  ComponentType,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useUserlens } from "userlens-analytics-sdk/react";

import { useAppDispatch } from "src/store";
import { AGENT_THREAD_FLOW } from "src/constants";
import { selectUserId, selectCompanyId, selectWhiteLabel } from "src/store/selectors";
import { updateSyncTrackersCollection } from "src/store/actions";

import styles from "./Agent.module.scss";
import { useAgent } from "src/context/AgentContext";
import { ChatLayout } from "../CopilotKit/components";
import { useCoAgent, useCoAgentStateRender, useCopilotChat } from "@copilotkit/react-core";
import {
  REACT_APP_API_BASE_URL,
  REACT_APP_LANGGRAPH_MODEL_NAME,
} from "../../store/threads/threadsApi";
import { AssistantSearchShareLineChartTool, AssistantSearchSharePieChartTool, AssistantVolumeShareLineChartTool, AssistantVolumeSharePieChartTool } from "./components/AssistantTools/components";
import { Loader, Preloader } from "src/components";
import { renderMessage } from "./components/ChatThread/utils";
import {
  getRandomArrayItems,
  showToastNotification,
  triggerGtmEvent,
} from "src/utils";
import { createThread, getThreadById } from "src/store/threads/threadsSlice";
import isString from "lodash/isString";
import { parseWebResult } from "./utils";
import { SourcesGallery } from "./components/Sources/SourceGallery";
import { addTrackers } from "src/store/trackers/trackersSlice";
import { getTrackersByIds } from "src/store/trackers/trackersApi";
import { getTrackersCollectionById } from "src/store/trackersCollections/trackersCollectionsApi";

type Props = {
  agentFlow: Thread.AgentId;
  configuration?: Record<string, unknown>;
  welcomeSignal?: number;
  isAgentDisplayMode?: boolean;
};

function isTalkToTheDataAgent(agentId: "research" | "talk_to_the_data") {
  return agentId === "talk_to_the_data";
}

type ToolConfig = {
  actionName: string;
  logMessage: string;
  Component: ComponentType<any>;
};

const TOOL_CONFIGS: ToolConfig[] = [
  {
    actionName: "generate_search_volume_line_chart_tool",
    logMessage: "Preparing data for search volume line chart",
    Component: AssistantVolumeShareLineChartTool,
  },
  {
    actionName: "generate_search_volume_pie_chart_tool",
    logMessage: "Preparing data for search volume pie chart",
    Component: AssistantVolumeSharePieChartTool,
  },
  {
    actionName: "generate_share_of_search_line_chart_tool",
    logMessage: "Preparing data for share of search line chart",
    Component: AssistantSearchShareLineChartTool,
  },
  {
    actionName: "generate_share_of_search_pie_chart_tool",
    logMessage: "Preparing data for share of search pie chart",
    Component: AssistantSearchSharePieChartTool,
  },
];

export const Agent: FC<Props> = ({
  agentFlow,
  configuration,
  isAgentDisplayMode,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useSelector(selectUserId);
  const companyId = useSelector(selectCompanyId);

  const whiteLabel = useSelector(selectWhiteLabel);
  const agentUrl = process.env.REACT_APP_LANGGRAPH_URL!;

  const { threadId, setAgent, selectedThreadId, onStartNewThread, showWelcomeMessage, setShowWelcomeMessage } = useAgent()

  const { reset } = useCopilotChat()
  const { collector } = useUserlens();

  const [hasCreatedThread, setHasCreatedThread] = useState(false);
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(false)
 
  useEffect(() => {
    setHasCreatedThread(false);
  }, [threadId]);

  useEffect(() => {
    setAgent(agentFlow); 
    reset(); 
    onStartNewThread();
  }, [agentFlow]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let cancelled = false;

    const loadThread = async () => {
      if (selectedThreadId) {
        try {
          reset();
          setLoading(true)

          const thread = await dispatch(
            getThreadById({ threadId: selectedThreadId! })
          ).unwrap();

          if (cancelled) return;
          if (thread?.values?.messages) {
            const messages = thread?.values?.messages;
            if (Array.isArray(messages) && messages.length > 0) {
              const message = messages[0];
              setTitle(message?.content);
            }
          }
          setShowWelcomeMessage(false)
        } catch (e) {
          if (cancelled) return;
          console.error("Failed to load thread:", e);
        } finally {
          if (cancelled) return;
          timeoutId = setTimeout(() => {
            setLoading(false)
          }, 1000)
        }
      }
    };

    loadThread();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedThreadId, dispatch]);

  useEffect(() => {
    lastProcessedAddTopicKeyRef.current = null;
    lastProcessedDashboardCreationRef.current = null;
  }, [threadId]);

  const createNewThread = async (message: any) => {
    try {
      const threadTitle = selectedThreadId !== '' && selectedThreadId === threadId ? 
      title : (isString(message) ? message : t("component.agent.label.untitled_thread"))

      await dispatch(
        createThread({
          agentUrl,
          threadId,
          flowType: '',
          companyId,
          messages: [],
          updatedAt: "",
          createdAt: "",
          deletedAt: null,
          graphId: agentId,
          authorId: userId,
          title: threadTitle,
          dashboardId: dashboardId || "",
        }),
      ).unwrap();
    } catch (e) {
    console.error(e);
  }
}

  const isDefaultDomain = useMemo<boolean>(
    () => whiteLabel.name === "MyTelescope",
    [whiteLabel.name],
  );
  
  // Get agent configuration
  const { id: agentId, flowTypes } = AGENT_THREAD_FLOW[agentFlow];

  // @ts-ignore
  const { dashboardId, dashboardName, topics, whiteLabelDomainId, authorId } =
    configuration;

  const configurable = {
    apiURL: REACT_APP_API_BASE_URL,
    model_name: REACT_APP_LANGGRAPH_MODEL_NAME,
    whiteLabelDomainId,
    companyId,
    authorId,
    graph_id: agentId,
    agentName: agentId,
    dashboardId,
    dashboardName,
    topics,
  };

  const {
    suggestions,
    welcomeMessage,
    welcomeSubMessages,
    suggestionCountToShow,
  } = useMemo(() => AGENT_THREAD_FLOW[agentFlow], [agentFlow]);

  const { start, running, run, state, setState } = useCoAgent({
    name: agentId,
    config: {
      configurable,
    },
  });

  const lastProcessedAddTopicKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const dashboardId = configuration?.dashboardId as string | undefined;
    if (!dashboardId || !isTalkToTheDataAgent(agentId)) return;

    const addTopicMessage = state?.messages?.findLast?.(
      (m: any) => m?.actionName === "add_topic_tool" && m?.result,
    );

    if (!addTopicMessage?.result) return;

    const resultKey = `${addTopicMessage.id}-${addTopicMessage.actionName}`;

    if (lastProcessedAddTopicKeyRef.current === resultKey) {
      return;
    }
    lastProcessedAddTopicKeyRef.current = resultKey;

    let result: any;
    try {
      result =
        typeof addTopicMessage.result === "string"
          ? JSON.parse(addTopicMessage.result)
          : addTopicMessage.result;
    } catch (e) {
      console.error("Failed to parse add_topic_tool result:", e);
      return;
    }

    if (!result?.success) return;

    const newTrackerIds: string[] = Array.isArray(result?.results)
      ? result.results
      : [];

    const handleAddTopic = async () => {
      try {
        const newTrackers: Tracker.Data[] = await getTrackersByIds(
          newTrackerIds,
        );
        dispatch(addTrackers(newTrackers));

        const trackersCollection = await getTrackersCollectionById(dashboardId);

        dispatch(
          updateSyncTrackersCollection({
            id: trackersCollection.id,
            changes: trackersCollection,
          }),
        );
      } catch (error) {
        console.error(error);
        showToastNotification({
          type: "error",
          text: t("common.error.server_error"),
        });
      }
    };

    handleAddTopic();
  }, [dispatch, state?.messages, configuration?.dashboardId, agentId, t]);

  const lastProcessedDashboardCreationRef = useRef<string | null>(null);

  useEffect(() => {
    if (isTalkToTheDataAgent(agentId)) return;

    const createDashboardMessage = state?.messages?.findLast?.(
      (m: any) => m?.actionName === "create_dashboard_tool" && m?.result
    );

    if (!createDashboardMessage?.result) return;

    const resultKey = `${createDashboardMessage.id}-${createDashboardMessage.actionName}`;

    if (lastProcessedDashboardCreationRef.current === resultKey) {
      return;
    }
    lastProcessedDashboardCreationRef.current = resultKey;

    collector?.pushEvent({
      event: "ask:create_dashboard_from_ask",
      properties: {
        result: createDashboardMessage.result,
      },
    });
  }, [state?.messages, agentFlow, collector, agentId]);

  if (running) {
    //stop();
  }

  /*const {stop} = useChat({
    copilotConfig: {chatApiEndpoint: "http://localhost:3000/", headers: {}}
    //config: {configurable}
  })*/
  const chatLayoutProps = {
    placeholder: isTalkToTheDataAgent(agentId) && !isAgentDisplayMode 
      ? t("component.agent.talk_to_data.input.ask_followup")
      : t("component.agent.research.input.placeholder"),
  };
  const onStopGeneration = ({ messages,
                              setMessages,
                              stopGeneration,
                              currentAgentName,
                              restartCurrentAgent,
                              stopCurrentAgent,
                              runCurrentAgent,
                              setCurrentAgentState  }: any) => {
   // stop();
    stopGeneration();
    // @ts-ignore
    //setMessages(messages.filter(message => message.role !== "assistant"))
    return;
  };

  //Display loaders and charts
  useCoAgentStateRender<{ status: string; state: Object; nodeName?: string }>(
    {
      name: agentId,
      render: ({ status, state, nodeName }: { status: string; state: any; nodeName?: string }) => {
        // 1) Logs        
        const logs = state?.copilotkit?.logs ?? [];

        // 2) Chart payload for the specific tool
        const hasLog = (message: string) =>
          logs.some((l: any) => l.message === message || l.message.includes(message)) ;
        
        const chartMsg = TOOL_CONFIGS.map(config => {
          const msg = state?.messages?.findLast((m: any) => m.actionName === config.actionName);
          const shouldDisplay = hasLog(config.logMessage);
          return msg && shouldDisplay ? { config, msg } : null;
        }).filter(Boolean);

        // 3) Sources
        const raw = state?.messages?.findLast((m: any) => m.actionName === "web_search_tool")?.result; 
        const sources = parseWebResult(raw ?? "");
        const shouldDisplaySources = hasLog("Searching the web");
        
        const visibleLogs = (logs ?? []).filter((log: any) => log.message?.trim());
  
        return (
          <>
            {visibleLogs.length > 0 && (
              <div className={styles.logs}>
                {visibleLogs.map((log: any, i: number) => (
                    <div key={i}>
                      {log.done ? '✓' : <Loader />} {log.message} {log.done ? '' : '...'}
                    </div>
                  ))}
              </div>
            )}
  
            {chartMsg.map(({ config, msg }: any) => {
              const Comp = config.Component;
              return (
                <div className={styles.chartWrapper} key={config.actionName}>
                  <Comp {...msg} dashboardId={configuration?.dashboardId} />
                </div>
              )
            })}

            {shouldDisplaySources && sources.length > 0 && (
              <SourcesGallery sources={sources}/>
            )}
          </>
        );
      },
    },
    [agentId, configuration?.dashboardId]
  );

  const suggestedPrompts = useMemo(() => {
    const base = Array.isArray(suggestions) ? suggestions : [];
    const newBase = agentId !== 'research' ? getRandomArrayItems(base, suggestionCountToShow) : base;
    const result = newBase.map((s) =>
      typeof s === "string" ? { title: s, message: s } : s
    );
    return result;
  }, [onStartNewThread, suggestions, suggestionCountToShow]);

  const onSubmitMessage = (message: any) => {
    setShowWelcomeMessage(false);
    
    const matchedSuggestion = suggestedPrompts.find(
      (s) => s.message === message || s.title === message
    );
    
    if (matchedSuggestion) {
      const eventMap: Record<string, string> = {
        "Share of Search": "AgentSuggestionShareOfSearch",
        "Brand Tracking": "AgentSuggestionBrandTracking",
      };
      
      const eventName = eventMap[matchedSuggestion.title];

      if (eventName) {
        triggerGtmEvent(eventName as any);
      }
    }
    
    if (!hasCreatedThread) {
      createNewThread(message);
      setHasCreatedThread(true);
    }
    const agentMode = isTalkToTheDataAgent(agentId) ? "dashboard" : "ask";
    collector?.pushEvent({
      event: `${agentMode}:chat_entered`,
      properties: {
        prompt: message,
      },
    });
  };

    return (
      <div className={styles.container}>
        {loading && <Preloader modifier="global" className={styles.initialPreloader} />}
        {!loading &&showWelcomeMessage ? ( 
          <div className={cx(styles.root, styles.threadRoot, styles[agentFlow])}>
            <div className={styles.threadViewport}>
              <div className={styles.welcomeRoot}>
                <div className={styles.welcomeCenter}>
                  <div className={cx(styles.avatarRoot, isDefaultDomain && styles.avatarRootDefault)}>
                    <img
                      alt={whiteLabel.name}
                      src={whiteLabel.logos.small}
                      className={styles.avatarImage}
                    />
                  </div>
                  <div className={cx(
                    styles.welcomeMessage,
                    isTalkToTheDataAgent(agentFlow) && styles.welcomeMessageDashboard)}
                  >
                    {isAgentDisplayMode ? t("component.agent.talk_to_data.title_agent_mode") : renderMessage(welcomeMessage, configuration)}
                    <div className={styles.welcomeSubmessages}>
                      {welcomeSubMessages?.map((message) => (
                      <div
                        key={message}
                        className={styles.welcomeSubmessage}
                      >
                        {renderMessage(message, configuration)} {isAgentDisplayMode ? "" : "Dashboard"}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.welcomeChat}>
                {!loading && <ChatLayout
                  key={`welcome-${agentId}-${threadId}`}
                  chatLayoutProps={chatLayoutProps}
                  placeholder={chatLayoutProps.placeholder}
                  onStopGeneration={onStopGeneration}
                  onSubmitMessage={onSubmitMessage}
                  suggestions={suggestedPrompts}
                  showSuggestions
                />}
              </div>
             </div>
            </div>
          </div>
        ) : (
          !loading && <ChatLayout
            key={`chat-${selectedThreadId || 'new-thread'}-${agentId}`}
            chatLayoutProps={chatLayoutProps}
            placeholder={chatLayoutProps.placeholder}
            onStopGeneration={onStopGeneration}
            onSubmitMessage={onSubmitMessage}
          />
        )}
      </div>
    );
};
